import React, { FC, useCallback, useEffect, useMemo } from 'react';
import {
  Control,
  FieldErrors,
  useFieldArray,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import { CalendarIcon } from '../../../../assets/icons/index';
import { AnimatedNumber } from '../../../../components/animated-number/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { OverlayDatePicker as DatePicker } from '../../../../components/date-picker/index';
import { DropDown } from '../../../../components/dropdown/index';
import { FormInput } from '../../../../components/input/index';
import { Stakeholder, useFormat, useLocale } from '../../../../hooks/index';
import { EventFormType, EventUnionWithPermissions } from '../../../../types/events.types';
import { toNumber } from '../../../../utils/to-number.util';
import {
  FormDropDown,
  ShareClassCombobox,
  StakeholderCombobox,
  SubEventFileUpload,
} from '../components/index';
import { FormSchema } from '../event-form.validation';
import { resolveTransactionItemDocusignFileLinks } from '../utils/resolve-linked-signature-file-links.util';

export type BuyBackStepOneProps = {
  clearErrors: UseFormClearErrors<FormSchema>;
  companyId: string;
  control: Control<FormSchema>;
  errors: FieldErrors<FormSchema>;
  event: EventUnionWithPermissions | null;
  formData: UseFormGetValues<FormSchema>;
  setError: UseFormSetError<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
};

export const BuyBackStepOne: FC<BuyBackStepOneProps> = ({
  control,
  setFormData,
  companyId,
  clearErrors,
  errors,
  formData,
  setError,
  event,
}) => {
  const { messagesLocale } = useLocale();
  const { initialStep } = useWatch<FormSchema>({ control });

  const defaultDate = useMemo(() => new Date(+0), []);

  const {
    fields: buyBackItemsFields,
    append: buyBackItemsAppend,
    remove: buyBackItemsRemove,
  } = useFieldArray({
    control,
    name: 'stepOne.eventDetails.buyback.buyBackItems',
  });

  const handleRemoveBuyBackItems = useCallback(
    (index: number) => {
      buyBackItemsRemove(index);
    },
    [buyBackItemsRemove],
  );

  const handleAppendBuyBackItems = useCallback(() => {
    buyBackItemsAppend({
      date: defaultDate,
      balance: 0,
      stakeholder: { id: '', fullName: '' },
      shareClass: { id: '', name: '' },
      shares: 0,
    });
  }, [buyBackItemsAppend, defaultDate]);

  useEffect(() => {
    if (initialStep?.type === EventFormType.BUYBACK && buyBackItemsFields.length === 0) {
      handleAppendBuyBackItems();
    }
  }, [buyBackItemsFields.length, handleAppendBuyBackItems, initialStep?.type]);

  return (
    <>
      {buyBackItemsFields.map((field, index) => (
        <BuyBackItem
          clearErrors={clearErrors}
          companyId={companyId}
          control={control}
          errors={errors}
          event={event}
          formData={formData}
          handleRemoveBuyBackItems={handleRemoveBuyBackItems}
          index={index}
          key={field.id}
          setError={setError}
          setFormData={setFormData}
          showRemoveButton={buyBackItemsFields.length > 1}
        />
      ))}
      <Button
        className="w-fit px-4 py-2 text-sm font-[450] text-brand-700 underline disabled:border-transparent disabled:bg-transparent"
        disabled={buyBackItemsFields.length >= 100}
        onClick={handleAppendBuyBackItems}
        styleType="NONE"
      >
        + <AppFormattedMessage id="ADD_ANOTHER_BUYBACK" />
      </Button>
      <DropDown
        answer={[
          messagesLocale['BUYBACK_EVENT_EXPLANATION_1'],
          messagesLocale['BUYBACK_EVENT_EXPLANATION_2'],
        ]}
        className="rounded-lg bg-gray-50"
        question={<AppFormattedMessage id="WHAT_IS_BUYBACK" />}
      />
    </>
  );
};

const BuyBackItem: FC<
  {
    handleRemoveBuyBackItems: (index: number) => void;
    index: number;
    showRemoveButton: boolean;
  } & BuyBackStepOneProps
> = ({
  control,
  setFormData,
  companyId,
  index,
  handleRemoveBuyBackItems,
  showRemoveButton,
  clearErrors,
  errors: { stepOne: errors },
  formData,
  setError,
  event,
}) => {
  const { stepOne } = useWatch<FormSchema>({ control });
  const { format } = useFormat();
  const { messagesLocale } = useLocale();

  const defaultDate = new Date(+0);

  const stakeholderId =
    stepOne?.eventDetails?.buyback?.buyBackItems?.[index]?.stakeholder?.id || '';
  const shareClassId = stepOne?.eventDetails?.buyback?.buyBackItems?.[index]?.shareClass?.id || '';
  const initialShares =
    stepOne?.eventDetails?.buyback?.buyBackItems?.[index]?.initialShares || undefined;
  const initialStakeholderId =
    stepOne?.eventDetails?.buyback?.buyBackItems?.[index]?.initialStakeholderId;
  const initialShareClassId =
    stepOne?.eventDetails?.buyback?.buyBackItems?.[index]?.initialShareClassId;
  const shouldAddInitialShares =
    initialShares && initialStakeholderId === stakeholderId && initialShareClassId === shareClassId;

  const { balance } = Stakeholder.useBalance({ companyId, stakeholderId, shareClassId });

  useEffect(() => {
    if (balance) {
      setFormData(
        `stepOne.eventDetails.buyback.buyBackItems.${index}.balance`,
        shouldAddInitialShares ? Number(balance) + initialShares : Number(balance),
      );
    }
  }, [balance, index, initialShares, setFormData, shouldAddInitialShares]);

  const transactionId = stepOne?.eventDetails?.buyback?.buyBackItems?.[index]?.id;
  const linkedDocusignFileLinks = resolveTransactionItemDocusignFileLinks(event, transactionId);

  const getFullTitle = (index: number) => {
    const stakeholderName =
      stepOne?.eventDetails?.buyback?.buyBackItems?.[index]?.stakeholder?.fullName;

    if (stakeholderName) {
      return `${messagesLocale['STAKEHOLDER']} - ${stakeholderName}`;
    }

    return messagesLocale['STAKEHOLDER'];
  };

  const calculateBalance = useCallback(
    (index: number) => {
      if (balance) {
        const currentItem = stepOne?.eventDetails?.buyback?.buyBackItems?.[index];

        if (!currentItem) {
          return Number(balance);
        }

        const filteredItems =
          stepOne?.eventDetails?.buyback?.buyBackItems &&
          stepOne?.eventDetails?.buyback?.buyBackItems.filter((_, i) => i !== index);

        if (!filteredItems || filteredItems.length === 0) {
          return Number(balance);
        }

        const matchingItems = filteredItems.filter(
          (item) =>
            item.stakeholder?.id === currentItem.stakeholder?.id &&
            item.shareClass?.id === currentItem.shareClass?.id,
        );

        if (matchingItems.length === 0) {
          return Number(balance);
        }

        const totalShares = matchingItems.reduce((sum, item) => sum + toNumber(item.shares), 0);

        return Number(balance - totalShares);
      }
      return 0;
    },
    [balance, stepOne?.eventDetails?.buyback?.buyBackItems],
  );

  const calculateAvailableShares = useCallback(
    (index: number) => {
      const shares = stepOne?.eventDetails?.buyback?.buyBackItems?.[index]?.shares || 0;
      const balance = shouldAddInitialShares
        ? calculateBalance(index) + (initialShares || 0)
        : calculateBalance(index);

      return balance - shares;
    },
    [
      calculateBalance,
      initialShares,
      shouldAddInitialShares,
      stepOne?.eventDetails?.buyback?.buyBackItems,
    ],
  );

  return (
    <FormDropDown
      handleRemove={() => handleRemoveBuyBackItems(index)}
      showRemoveButton={showRemoveButton}
      title={getFullTitle(index)}
    >
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full flex-col gap-3">
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="EVENT_DATE" />
            </span>
            <DatePicker
              onSelect={(date) =>
                setFormData(`stepOne.eventDetails.buyback.buyBackItems.${index}.date`, date)
              }
              value={stepOne?.eventDetails?.buyback?.buyBackItems?.[index]?.date}
              wrapperClassName="w-[224px]"
            >
              <FormInput
                autoComplete="off"
                control={control}
                disabledInput
                icon={<CalendarIcon className="mr-2 size-6" iconColor="#98A2B3" />}
                name={`stepOne.eventDetails.buyback.buyBackItems.${index}.date`}
                placeholder={<AppFormattedMessage id="DATE" />}
                transform={(value) => {
                  return value && new Date(value).getTime() !== defaultDate.getTime()
                    ? format(value, 'dd/MM/yyyy')
                    : '';
                }}
                wrapperClassName="w-full"
              />
            </DatePicker>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="STAKEHOLDER" />
            </span>
            <StakeholderCombobox
              companyId={companyId}
              control={control}
              name={`stepOne.eventDetails.buyback.buyBackItems.${index}.stakeholder`}
              onlyWithShares
            />
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="SHARE_CLASS" />
            </span>
            <ShareClassCombobox
              companyId={companyId}
              control={control}
              fieldName={`stepOne.eventDetails.buyback.buyBackItems.${index}.shareClass`}
              stakeholderFromId={
                stepOne?.eventDetails?.buyback?.buyBackItems?.[index]?.stakeholder?.id
              }
            />
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="SHARES" />
            </span>
            <div className="flex flex-col items-start gap-2">
              <FormInput
                control={control}
                name={`stepOne.eventDetails.buyback.buyBackItems.${index}.shares`}
                numberOnly
                placeholder={<AppFormattedMessage id="VALUE" />}
                shouldFormatNumber
                wrapperClassName="w-[224px]"
              />
              {balance && (
                <span className="text-xs text-gray-600">
                  <AppFormattedMessage id="AVAILABLE" />{' '}
                  <AnimatedNumber integer value={Math.max(calculateAvailableShares(index), 0)} />
                </span>
              )}
            </div>
          </div>
        </div>
        <SubEventFileUpload
          clearErrors={clearErrors}
          companyId={companyId}
          errors={errors?.eventDetails?.buyback?.buyBackItems?.[index]?.files || undefined}
          field={`stepOne.eventDetails.buyback.buyBackItems.${index}.files`}
          files={stepOne?.eventDetails?.buyback?.buyBackItems?.[index]?.files || []}
          formData={formData}
          linkedDocusignFileLinks={linkedDocusignFileLinks}
          setError={setError}
          setFormData={setFormData}
          text="buyback"
        />
      </div>
    </FormDropDown>
  );
};
